
import React, { useMemo } from 'react';
import { NewsItem, NewsCategory, Banner, Subscriber, SiteSettings } from '../types';
import { Calendar, ArrowRight, Tag, Zap, ChevronRight, Megaphone, X } from 'lucide-react';
import { getBasePath, fixImageUrl, formatDatePtBr } from '../constants';
import { slugify } from '../src/lib/utils';

// Helper functions for YouTube processing
function getYoutubeId(url: string | undefined): string | null {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/|live\/)([^#\&\?]*).*/;
  const match = url.trim().match(regExp);
  if (match && match[2] && match[2].length === 11) {
    return match[2];
  }
  return null;
}

function extractFirstYoutubeUrl(htmlContent: string | undefined): string | null {
  if (!htmlContent) return null;
  
  // Look for iframe src
  const iframeMatch = htmlContent.match(/src="[^"]*?(?:youtube\.com|youtu\.be)\/(?:embed\/)([a-zA-Z0-9_-]{11})/);
  if (iframeMatch) {
    return `https://www.youtube.com/watch?v=${iframeMatch[1]}`;
  }

  // Look for any raw youtube URLs or wrapped in href
  const urlRegExp = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/live\/)[a-zA-Z0-9_-]{11})/i;
  const match = htmlContent.match(urlRegExp);
  if (match) {
    return match[1];
  }
  return null;
}

// Ícone Oficial do WhatsApp
const WhatsappIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

interface NewsSectionProps {
  news: NewsItem[];
  categories: NewsCategory[];
  settings: SiteSettings; // Adicionado
  banners?: Banner[];
  onViewAll: (category?: string) => void;
  onReadArticle: (article: NewsItem) => void;
  onSubscribe?: (data: Omit<Subscriber, 'id' | 'date'>) => void;
  activeArticleCategory?: string;
  activeArticleId?: string;
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string; accent: string; light: string; title: string }> = {
  'tecnologia': { bg: 'bg-blue-600', text: 'text-blue-600', border: 'border-blue-200', accent: 'bg-blue-50/50', light: 'bg-blue-500/10', title: 'text-blue-900' },
  'design': { bg: 'bg-purple-600', text: 'text-purple-600', border: 'border-purple-200', accent: 'bg-purple-50/50', light: 'bg-purple-500/10', title: 'text-purple-900' },
  'seo': { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', accent: 'bg-emerald-50/50', light: 'bg-emerald-500/10', title: 'text-emerald-900' },
  'negocios': { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', accent: 'bg-amber-50/50', light: 'bg-amber-500/10', title: 'text-amber-900' },
  'saude': { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', accent: 'bg-rose-50/50', light: 'bg-purple-500/10', title: 'text-rose-900' },
  'default': { bg: 'bg-slate-800', text: 'text-slate-800', border: 'border-slate-200', accent: 'bg-slate-50/50', light: 'bg-slate-500/10', title: 'text-slate-900' }
};

const GOOGLE_THEMES = [
  { bg: 'bg-blue-600', text: 'text-blue-600', border: 'border-blue-100', accent: 'bg-blue-50', light: 'bg-blue-100', title: 'text-blue-900' },
  { bg: 'bg-red-600', text: 'text-red-600', border: 'border-red-100', accent: 'bg-red-50', light: 'bg-red-100', title: 'text-red-900' },
  { bg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-100', accent: 'bg-amber-50', light: 'bg-amber-100', title: 'text-amber-900' },
  { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-100', accent: 'bg-emerald-50', light: 'bg-emerald-100', title: 'text-emerald-900' },
];

const getCategoryTheme = (slug: string, index?: number) => {
    if (typeof index === 'number') {
        return GOOGLE_THEMES[index % GOOGLE_THEMES.length];
    }
    return CATEGORY_COLORS[slug] || CATEGORY_COLORS['default'];
};

const SectionSeparator = () => (
  <div className="relative py-8 md:py-12 flex items-center justify-center">
    <div className="absolute inset-0 flex items-center" aria-hidden="true">
      <div className="w-full border-t border-gray-200/40"></div>
    </div>
    <div className="relative flex justify-center bg-white px-4 md:px-6">
      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full mx-1"></div>
      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full mx-1"></div>
      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full mx-1"></div>
    </div>
  </div>
);

export const NewsCard: React.FC<{ item: NewsItem, theme: any, settings: SiteSettings, onClick: () => void, categorySlug?: string, isHorizontal?: boolean }> = ({ item, theme, settings, onClick, categorySlug, isHorizontal = false }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const cleanWhatsApp = settings.whatsappNumber?.replace(/\D/g, '') || '5516992091604';
  const base = getBasePath();
  const prefix = base === '/' ? '' : base;
  const baseUrl = settings?.installationUrl ? settings.installationUrl.replace(/\/$/, '') : (window.location.origin + prefix);
  
  const catSlug = categorySlug || slugify(item.category);
  const articleUrl = `${baseUrl}/${encodeURIComponent(catSlug)}/${encodeURIComponent(item.slug || item.id)}`;
  
  const message = encodeURIComponent(`Olá! Vi esta matéria no site e gostaria de saber mais: *${item.title}*. \n\nLink: ${articleUrl}`);
  const whatsappUrl = `https://wa.me/${cleanWhatsApp}?text=${message}`;

  const hasVideoThumbnail = !!item.video_thumbnail_enabled;
  const targetVideoUrl = item.video_thumbnail_url || extractFirstYoutubeUrl(item.content);
  const youtubeId = hasVideoThumbnail && targetVideoUrl ? getYoutubeId(targetVideoUrl) : null;
  const embedUrl = youtubeId ? `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=0&rel=0` : null;

  return (
    <article 
        className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-primary/20 transition-all hover:shadow-xl flex ${isHorizontal ? 'flex-col md:flex-row w-full' : 'flex-col w-full max-w-md mx-auto'} h-full transform hover:-translate-y-1 duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.03)]`}
    >
        <div className={`relative ${isHorizontal ? 'h-48 md:h-auto md:w-2/5 min-h-[220px]' : 'h-44 md:h-48'} overflow-hidden bg-gray-100 shrink-0`}>
            {isPlaying && embedUrl ? (
                <div className="absolute inset-0 z-30 bg-black">
                    <iframe 
                        src={embedUrl} 
                        className="w-full h-full border-0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                        allowFullScreen
                    ></iframe>
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsPlaying(false);
                        }}
                        className="absolute top-2 right-2 z-40 bg-black/70 hover:bg-black text-white p-1 rounded-full border border-white/10 transition-transform active:scale-90"
                        title="Fechar Vídeo"
                    >
                        <X size={14} />
                    </button>
                </div>
            ) : null}

            {/* Normal thumbnail or video poster overlay */}
            <div className="w-full h-full cursor-pointer" onClick={onClick}>
                <img src={fixImageUrl(item.imageUrl, settings)} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60"></div>
                
                {/* Play button overlay */}
                {embedUrl && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/25 transition-colors z-20">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsPlaying(true);
                            }}
                            className="w-12 h-12 bg-rose-600 hover:bg-rose-700 text-white rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 transform hover:scale-110 active:scale-95 border border-white/20 animate-pulse"
                            title="Dar Play no Vídeo de Destaque"
                        >
                            <svg className="w-5 h-5 fill-current ml-0.5" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                )}

            </div>
        </div>
        <div className="p-4 md:p-6 flex flex-col flex-grow">
        <div className="flex items-center justify-between gap-2 mb-2.5 md:mb-3 flex-wrap">
            <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                <Calendar size={12} className={theme.text} />
                {formatDatePtBr(item.date)}
            </div>
            <span className={`px-2 py-0.5 ${theme.bg} text-white text-[9px] md:text-[10px] font-bold uppercase tracking-wider rounded`}>
                {item.category}
            </span>
        </div>
        <h3 className="text-base md:text-lg font-black text-gray-900 mb-2 md:mb-3 line-clamp-3 group-hover:text-primary transition-colors leading-tight tracking-tight cursor-pointer" onClick={onClick}>
            {item.title}
        </h3>
        <p className="text-gray-500 text-[13px] md:text-sm line-clamp-4 mb-4 md:mb-6 flex-grow leading-relaxed font-medium">
            {item.excerpt}
        </p>
        
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-50 pt-4">
            <button 
                onClick={onClick}
                className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${theme.text} opacity-80 group-hover:opacity-100 transition-opacity whitespace-nowrap`}
            >
                Ler Agora <ChevronRight size={12} className="group-hover:translate-x-1 transition-transform" />
            </button>
            
            <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm flex items-center gap-1.5 shrink-0"
                title="Saber mais no WhatsApp"
            >
                <span className="text-[9px] font-black uppercase hidden md:inline">Dúvidas?</span>
                <WhatsappIcon size={14} />
            </a>
        </div>
        </div>
    </article>
  );
};

const BannerComponent: React.FC<{ banner: Banner; settings?: SiteSettings }> = ({ banner, settings }) => (
    <div className="animate-fade-in-up">
        <a 
            href={banner.link} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="block w-full group"
        >
            <div 
                className="relative w-full rounded-2xl md:rounded-[24px] overflow-hidden shadow-xl shadow-slate-200 border border-gray-100"
                style={{ aspectRatio: '19 / 4' }}
            >
                <img 
                    src={fixImageUrl(banner.imageUrl, settings)} 
                    alt={banner.title} 
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
                />
            </div>
        </a>
        <SectionSeparator />
    </div>
);

const NewsSection: React.FC<NewsSectionProps> = ({ news, categories, settings, banners, onViewAll, onReadArticle, activeArticleCategory, activeArticleId }) => {
  const publicNews = news.filter(n => {
    const isLocalSeoFlag = n.is_local_seo === true || (n.is_local_seo as any) === 1 || (n.is_local_seo as any) === '1';
    const hasLocalSeoTag = n.tags?.some(tag => {
      const t = tag.toLowerCase().trim();
      return t === 'local-seo' || t === 'seo-local' || t === 'seo local' || t === 'seo_local' || t === 'cluster' || t === 'pilar';
    });
    const isLocalSeoCategory = n.category?.toLowerCase().trim() === 'seo local' || n.category?.toLowerCase().trim() === 'local seo';
    
    // Show if not draft AND NOT local SEO
    const baseCondition = n.status !== 'draft' && !isLocalSeoFlag && !hasLocalSeoTag && !isLocalSeoCategory;

    if (activeArticleCategory) {
      const isSameCategory = n.category?.toLowerCase().trim() === activeArticleCategory.toLowerCase().trim();
      const isCurrentArticle = n.id === activeArticleId || n.slug === activeArticleId;
      return baseCondition && isSameCategory && !isCurrentArticle;
    }

    return baseCondition;
  });

  if (publicNews.length === 0) {
    const skeletonTheme = { accent: 'bg-indigo-50/20', border: 'border-indigo-50', text: 'text-indigo-400', title: 'text-indigo-950', light: 'bg-indigo-500/10' };
    return (
      <section className="pt-0 pb-6 md:pt-0 md:pb-10 space-y-12 animate-pulse">
        <div className={`rounded-[24px] md:rounded-[32px] p-4 md:p-10 ${skeletonTheme.accent} border border-indigo-100`}>
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div className="flex items-center gap-4 md:gap-5">
              <div className="w-10 h-10 md:w-14 md:h-14 bg-slate-200 rounded-xl"></div>
              <div className="space-y-2">
                <div className="w-16 h-3 bg-slate-200/80 rounded"></div>
                <div className="w-32 h-6 bg-slate-200 rounded"></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-11">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 flex flex-col h-full shadow-[0_4px_12px_rgba(0,0,0,0.01)] p-4 space-y-4">
                <div className="w-full h-44 md:h-48 bg-slate-100 rounded-xl relative overflow-hidden">
                  <div className="absolute top-3 left-3 w-16 h-5 bg-slate-200 rounded"></div>
                </div>
                <div className="space-y-3 flex-grow p-2">
                  <div className="w-24 h-3 bg-slate-150 rounded"></div>
                  <div className="w-full h-5 bg-slate-200 rounded"></div>
                  <div className="w-4/5 h-5 bg-slate-200 rounded"></div>
                  <div className="w-full h-4 bg-slate-100 rounded"></div>
                  <div className="w-3/4 h-4 bg-slate-100 rounded"></div>
                </div>
                <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                  <div className="w-20 h-4 bg-slate-150 rounded"></div>
                  <div className="w-8 h-8 bg-slate-150 rounded-lg"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (activeArticleCategory) {
    const theme = getCategoryTheme(activeArticleCategory.toLowerCase());
    const relatedItems = publicNews.slice(0, 9);
    const hasOnlyOne = relatedItems.length === 1;
    return (
      <section className="pt-0 pb-6 md:pt-0 md:pb-10 space-y-12 animate-fade-in-up lg:max-w-[67%] w-full">
        <div className={`rounded-[24px] md:rounded-[32px] p-4 md:p-10 ${theme.accent} border ${theme.border}`}>
          <div className="flex items-center justify-between mb-8 md:mb-10">
            <div className="flex items-center gap-4 md:gap-5">
              <div className={`w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-200 transform -rotate-3`}>
                <Zap size={20} className="md:w-6 md:h-6" />
              </div>
              <div>
                <span className={`text-[9px] md:text-[10px] font-black ${theme.text} uppercase tracking-[0.2em] mb-0.5 md:1 block`}>Recomendado para Você</span>
                <h2 className={`text-xl md:text-4xl font-black ${theme.title} tracking-tighter uppercase leading-none`}>Matérias Relacionadas</h2>
                <p className="text-xs text-gray-500 mt-1">Mais conteúdos da categoria <strong className="text-primary font-black uppercase">{activeArticleCategory}</strong> para você continuar lendo</p>
              </div>
            </div>
          </div>
          <div className={hasOnlyOne ? "w-full" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 md:gap-10"}>
            {relatedItems.map(item => {
              const cat = categories.find(c => c.name.toLowerCase() === item.category.toLowerCase());
              return <NewsCard key={item.id} item={item} theme={theme} settings={settings} onClick={() => onReadArticle(item)} categorySlug={cat?.slug} isHorizontal={hasOnlyOne} />;
            })}
          </div>
        </div>
      </section>
    );
  }

  const latestPosts = publicNews.slice(0, 3);
  const latestHasOnlyOne = latestPosts.length === 1;
  const promotionalBanner = banners?.find(b => b.format === 'horizontal' && !b.categoryId);
  
  const categorizedNews = useMemo(() => {
    return categories.map(cat => {
      const items = publicNews.filter(n => n.category.toLowerCase() === cat.name.toLowerCase());
      return {
        category: cat,
        items: items.slice(0, 3)
      };
    }).filter(group => group.items.length > 0);
  }, [publicNews, categories]);

  const latestTheme = { accent: 'bg-indigo-50/40', border: 'border-indigo-100', text: 'text-indigo-600', title: 'text-indigo-900' };

  return (
    <section className="pt-0 pb-6 md:pt-0 md:pb-10 space-y-12 md:space-y-20">
      {/* SEÇÃO ÚLTIMOS POSTS */}
      <div className={`animate-fade-in-up rounded-[24px] md:rounded-[32px] p-4 md:p-10 ${latestTheme.accent} border ${latestTheme.border}`}>
        <div className="flex items-center justify-between mb-8 md:mb-10">
            <div className="flex items-center gap-4 md:gap-5">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-200 transform -rotate-3">
                    <Zap size={20} className="md:w-6 md:h-6" />
                </div>
                <div>
                    <span className={`text-[9px] md:text-[10px] font-black ${latestTheme.text} uppercase tracking-[0.2em] mb-0.5 md:1 block`}>Em Alta</span>
                    <h2 className={`text-xl md:text-4xl font-black ${latestTheme.title} tracking-tighter uppercase leading-none`}>Últimos Posts</h2>
                </div>
            </div>
            <button onClick={() => onViewAll()} className={`hidden sm:flex items-center gap-2 ${latestTheme.text} bg-white hover:bg-white/80 transition-all font-black text-xs uppercase tracking-widest px-6 py-3 rounded-lg shadow-sm hover:shadow-md border border-transparent`}>
                Ver Blog <ArrowRight size={16} />
            </button>
        </div>
        <div className={`grid gap-8 md:gap-12 justify-center ${
          latestPosts.length === 1 
            ? "grid-cols-1 max-w-4xl mx-auto" 
            : latestPosts.length === 2 
              ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto" 
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto"
        }`}>
            {latestPosts.map(item => {
                const cat = categories.find(c => c.name === item.category);
                return <NewsCard key={item.id} item={item} theme={getCategoryTheme(item.category.toLowerCase())} settings={settings} onClick={() => onReadArticle(item)} categorySlug={cat?.slug} isHorizontal={latestHasOnlyOne} />;
            })}
        </div>
        <button onClick={() => onViewAll()} className={`sm:hidden w-full mt-6 flex items-center justify-center gap-2 ${latestTheme.text} bg-white transition-all font-black text-[10px] uppercase tracking-widest py-4 rounded-xl shadow-sm border border-indigo-100`}>
            Acessar Blog Completo <ArrowRight size={14} />
        </button>
      </div>

      {promotionalBanner && <BannerComponent banner={promotionalBanner} settings={settings} />}

      {/* SEÇÕES CATEGORIZADAS */}
      <div className="space-y-12 md:space-y-16">
        {categorizedNews.map((group, index) => {
            const theme = getCategoryTheme(group.category.slug || group.category.name.toLowerCase(), index);
            const categoryBanner = banners?.find(b => b.categoryId === group.category.id && b.format === 'horizontal');
            const groupHasOnlyOne = group.items.length === 1;
            return (
            <React.Fragment key={group.category.id}>
                <div className={`animate-fade-in-up rounded-[24px] md:rounded-[32px] p-4 md:p-10 ${theme.accent} border ${theme.border}`}>
                    <div className="flex items-center justify-between mb-8 md:mb-10">
                        <div className="flex items-center gap-3 md:gap-4">
                            <div className={`w-9 h-9 md:w-12 md:h-12 bg-white rounded-lg md:rounded-xl flex items-center justify-center ${theme.text} transform rotate-3 shadow-sm border border-gray-100`}>
                                <Tag size={18} className="md:w-5 md:h-5" />
                            </div>
                            <div>
                                <h2 className={`text-lg md:text-3xl font-black ${theme.title} tracking-tighter uppercase leading-none`}>{group.category.name}</h2>
                                <p className={`text-[9px] md:text-xs font-bold ${theme.text} uppercase tracking-widest mt-0.5 md:mt-1 opacity-70`}>Destaques</p>
                            </div>
                        </div>
                        <button onClick={() => onViewAll(group.category.name)} className={`hidden sm:flex items-center gap-2 ${theme.text} bg-white hover:bg-white/80 transition-all font-black text-xs uppercase tracking-widest px-5 py-3 rounded-lg shadow-sm hover:shadow-md`}>Ver Tudo <ArrowRight size={14} /></button>
                    </div>
                    <div className={`grid gap-8 md:gap-10 justify-center ${
                      group.items.length === 1
                        ? "grid-cols-1 max-w-4xl mx-auto"
                        : group.items.length === 2
                          ? "grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto"
                          : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto"
                    }`}>
                        {group.items.map(item => <NewsCard key={item.id} item={item} theme={theme} settings={settings} onClick={() => onReadArticle(item)} categorySlug={group.category.slug} isHorizontal={groupHasOnlyOne} />)}
                    </div>
                    <button onClick={() => onViewAll(group.category.name)} className={`sm:hidden w-full mt-6 flex items-center justify-center gap-2 ${theme.text} bg-white transition-all font-black text-[10px] uppercase tracking-widest py-4 rounded-xl shadow-sm border ${theme.border}`}>
                        Ver Mais em {group.category.name} <ArrowRight size={14} />
                    </button>
                </div>
                {categoryBanner && <BannerComponent banner={categoryBanner} settings={settings} />}
            </React.Fragment>
            );
        })}
      </div>
    </section>
  );
};

export default NewsSection;
